// ============================================================================
// 位置名稱 → 總覽圖上的標示點編號
//
// 總覽圖（colon-preview-img.png / gastro-preview-img.png）上每一個可標示的位置
// 都有一組寫死的座標，定義在 FindingInfoByOrder / FindingInfoByOrgan 的
// `.highlight-N` CSS 裡。這份表就是 N ↔ 位置名稱的對照。
//
// ── 為什麼要有這一層 ────────────────────────────────────────────────────────
//
// 系統裡有**兩套**位置識別，id 空間完全不同：
//
//   landmarks 主檔      id 1~100+，名稱 31（大腸）/ 43（胃鏡）種，含 proximal /
//                       middle / distal 這類細分。截圖的 landmarkID 指向這裡。
//   總覽圖座標          1~16（大腸）/ 1~19（胃鏡），只到「解剖段」的粒度。
//
// 兩者用**名稱**接起來，不是 id——主檔的 id 是資料庫自動編號，會因為部署順序不同
// 而不一樣；名稱才是穩定的。這也是舊版 getLocationByLesionsObservation 的做法，
// 這裡只是把它從「解析 observation 字串」改成「直接吃位置名稱」。
//
// ⚠️ 表的內容原本各自寫在 FindingInfoByOrder / FindingInfoByOrgan 的 data() 裡。
//    搬到這裡是因為現在有第三個使用者（Report2Preview 要從截圖算出標示點），
//    三份各自維護必然分岔。那兩個元件改成 import 這裡，行為不變。
// ============================================================================

/** 大腸鏡總覽圖。key = `.highlight-N` 的 N */
export const COLON_LOCATION_MAP = {
  1: 'terminal ileum',
  2: 'ileocecal valve',
  3: 'appendix orifice',
  4: 'cecum',
  5: 'ascending colon',
  6: 'hepatic flexure',
  7: 'transverse colon',
  8: 'splenic flexure',
  9: 'descending colon',
  10: 'sigmoid colon',
  11: 'rs junction',
  12: 'rectum',
  13: 'anus',
  14: 'all segments of colon',
  15: 'anastomosis',
  16: 'anal retroflex',
}

/** 胃鏡總覽圖。key = `.highlight-N` 的 N */
export const GASTRO_LOCATION_MAP = {
  1: 'esophagus',
  2: 'cervical esophagus',
  3: 'upper esophagus',
  4: 'middle esophagus',
  5: 'lower esophagus',
  6: 'cardia',
  7: 'fundus',
  8: 'body',
  9: 'upper-body',
  10: 'high-body',
  11: 'mid-body',
  12: 'low-body',
  13: 'angle',
  14: 'antrum',
  15: 'bulb',
  16: 'sda',
  17: '2nd portion',
  18: 'papilla',
  19: 'ecj',
}

/**
 * 位置名稱正規化。
 *
 * - 底線 → 空白：主檔同時有 `appendix orifice` 與 `appendix_orifice` 兩種寫法
 * - `proximal` / `middle` / `distal` 前綴拿掉：總覽圖只到解剖段的粒度，
 *   `distal ascending colon` 與 `ascending colon` 標在同一個點
 *
 * ⚠️ 前綴只在「後面還有字」時才拿掉。胃鏡主檔有**單獨**的 `middle`（食道中段），
 *    無條件剝掉會變成空字串，那一筆就永遠對不到、而且是靜默的。
 */
function normalize(name) {
  const base = String(name || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const stripped = base.replace(/^(?:proximal|middle|distal)\s+/, '')
  return stripped || base
}

/**
 * 這個位置名稱要標在總覽圖的第幾號座標上。
 *
 * @param {string} name landmarks 主檔的 landmark 欄位
 * @param {Object} map COLON_LOCATION_MAP 或 GASTRO_LOCATION_MAP
 * @returns {number|null} 對不到時回 null——呼叫端要把它列在圖旁，不可以靜默丟掉
 */
export function locationHighlightIndex(name, map) {
  const normalized = normalize(name)
  if (!normalized) return null

  // 取**最長**的相符項，不是第一個。
  // `aw high-body` 同時包含 `body`(8) 與 `high-body`(10)，取第一個就會標到胃體
  // 而不是胃體上部。舊版的 getLocationByLesionsObservation 是把兩個都收下來
  // （所以同一個病灶會冒出兩個點），這裡收斂成最具體的那一個。
  let best = null
  let bestLen = 0
  for (const key of Object.keys(map)) {
    const value = map[key]
    if (normalized.indexOf(value) >= 0 && value.length > bestLen) {
      best = Number(key)
      bestLen = value.length
    }
  }
  return best
}
